import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { useField } from 'react-final-form';

import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IBooleanSelectOption } from 'KnmPackage/MainModule/interfaces/IBooleanSelectOption';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import {
  createEmptyViolationEvent,
  ErknmActViolationEventItems,
  getAvailablePassportEventOptions,
} from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmActViolationEventItems';
import { createPassportEventSelectOptions } from 'KnmErknmPackage/ErknmViolationsModule/common/createPassportEventSelectOptions';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import styles from './ErknmViolationItem.module.scss';
import { ReactQuillEditorField } from '../../../UIPackage/MainModule/components/ReactQuillEditorField';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IErknmActViolationInitItemProps {
  violation: IKnmErknmFormViolation;
  fieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  onRemove: () => void;
  isPaperless: boolean;
  id: string;
  watchOnly?: boolean;
  isDisabledByDirty?: boolean;
  hasUnsignedAct?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
}

const negativeSelectOption = booleanSelectOptions.find(option => !option.value);

export const ErknmActViolationInitItem = ({
  fieldName,
  violation,
  formRef,
  onRemove,
  isPaperless,
  id,
  watchOnly = false,
  isDisabledByDirty = false,
  hasUnsignedAct = false,
  isKnm,
  isPm,
  isDecision,
}: IErknmActViolationInitItemProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const isWatchOnly = watchOnly || isDisabledByDirty;

  if (!violation.isFixedOnPlace?.value && violation.explanation) {
    // @ts-ignore
    formRef?.current.change(`${fieldName}.explanation`);
  }

  let passportEventsFieldName: 'eventsPm' | 'events' | 'eventsErknm' = 'eventsErknm';

  if (isPm) {
    passportEventsFieldName = 'eventsPm';
  } else if (isDecision) {
    passportEventsFieldName = 'events';
  }

  const {
    input: { value: passportEvents },
  } = useField(passportEventsFieldName as keyof IKnmForm, { subscription: { value: true } });
  const passportEventsList: IKnmErknmFormEvent[] | undefined = passportEvents;

  const availablePassportEventOptions = useMemo(() => getAvailablePassportEventOptions(violation, passportEventsList), [
    violation,
    passportEventsList,
  ]);

  const handleHasViolationEvent = useCallback(
    (v: IBooleanSelectOption) => {
      // @ts-ignore
      formRef?.current.change(`${fieldName}.inEvent`, v);

      if (v?.value) {
        const firstOption = createPassportEventSelectOptions(passportEventsList)[0] || null;

        // @ts-ignore
        formRef?.current.change(`${fieldName}.events`, [createEmptyViolationEvent(firstOption)]);
      } else {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.events`, undefined);
      }
    },
    [formRef, fieldName, passportEventsList],
  );

  const handleAddViolationEvent = useCallback(() => {
    const nextOption = availablePassportEventOptions[0];

    if (!nextOption) {
      return;
    }

    formRef?.current.mutators.push(`${fieldName}.events`, createEmptyViolationEvent(nextOption));
  }, [availablePassportEventOptions, formRef, fieldName]);

  const canAddViolationEvent =
    !!violation.inEvent?.value && availablePassportEventOptions.length > 0 && !!hasUnsignedAct && !isWatchOnly;

  const hasError = checkHasErrorInCollapseBlock(formRef, fieldName);

  return (
    <KnmCollapse
      id={id}
      title={<span className="shared-break-word">Нарушение №{violation.number}</span>}
      bodyClassName={styles.ItemCollapseBody}
      onClose={violation.isNew ? onRemove : undefined}
      openByDefault={true}
      error={isKnmCollapseOpened && hasError}
      changeOpenedCallback={setIsKnmCollapseOpened}
    >
      <>
        <Row>
          <FieldBlock
            label={getTitleFieldWithRedStar({
              label: `Описание${!isPaperless ? ' выявленного нарушения' : ''}`,
              field: `${fieldName}.descriptionReactQuillEditor`,
              isPm,
              isKnm,
              isDecision,
            })}
            placementLabel="left"
            color={isPaperless ? 'black' : undefined}
            className={cn({ [styles.FieldBlock]: isPaperless })}
            labelSize="col-3"
            labelFontSize="small"
            type="not-space"
            id={`${id}.descriptionReactQuillEditor`}
          >
            <ReactQuillEditorField
              className={isPaperless ? '' : 'shared-row-field'}
              name={`${fieldName}.descriptionReactQuillEditor`}
              handleCustomChange={(e: string) => {
                // @ts-ignore
                formRef?.current?.change(`${fieldName}.title` as never, e);
              }}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            label={`${!isPaperless ? 'Нарушение н' : 'Н'}е подлежит устранению`}
            placementLabel="left"
            color={isPaperless ? 'black' : undefined}
            className={cn({ [styles.FieldBlock]: isPaperless })}
            labelSize="col-3"
            labelFontSize="small"
            type="not-space"
          >
            <FieldBlock size={!isPaperless ? 'col-3' : undefined}>
              <SelectInputField
                className={isPaperless ? '' : 'shared-row-field'}
                name={`${fieldName}.isUnfixable`}
                options={booleanSelectOptions}
                isClearable={false}
              />
            </FieldBlock>
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            label={`${!isPaperless ? 'Нарушение у' : 'У'}странено в ходе мероприятия`}
            placementLabel="left"
            color={isPaperless ? 'black' : undefined}
            className={cn({ [styles.FieldBlock]: isPaperless })}
            labelSize="col-3"
            labelFontSize="small"
            type="not-space"
          >
            <FieldBlock size={!isPaperless ? 'col-3' : undefined}>
              <SelectInputField
                className={isPaperless ? '' : 'shared-row-field'}
                name={`${fieldName}.isFixedOnPlace`}
                options={booleanSelectOptions}
                isClearable={false}
              />
            </FieldBlock>
          </FieldBlock>
        </Row>
        {violation.isFixedOnPlace?.value && (
          <Row>
            <FieldBlock
              label={!isPaperless ? 'Пояснение' : 'Комментарий'}
              placementLabel="left"
              color={isPaperless ? 'black' : undefined}
              className={cn({ [styles.FieldBlock]: isPaperless })}
              labelSize="col-3"
              labelFontSize="small"
              type="not-space"
            >
              <ReactQuillEditorField
                placeholder="Введите пояснение (при необходимости)"
                className={isPaperless ? '' : 'shared-row-field'}
                id={`${fieldName}.explanationReactQuillEditor`}
                name={`${fieldName}.explanationReactQuillEditor`}
                handleCustomChange={(e: string) => {
                  // @ts-ignore
                  formRef?.current?.change(`${fieldName}.explanation` as never, e);
                }}
              />
            </FieldBlock>
          </Row>
        )}
        {isPaperless && (
          <Row>
            <FieldBlock
              label="Выявлено в рамках действия"
              placementLabel="left"
              color="black"
              className={styles.FieldBlock}
              labelSize="col-3"
              labelFontSize="small"
              type="not-space"
              id={`${id}.inEvent`}
              button={
                canAddViolationEvent ? (
                  <Button
                    className="shared-btn-add"
                    text="Добавить"
                    type="button"
                    variant="link"
                    color="primary"
                    onClick={handleAddViolationEvent}
                  />
                ) : undefined
              }
            >
              <SelectInputField
                name={`${fieldName}.inEvent`}
                options={booleanSelectOptions}
                isClearable={false}
                defaultValue={negativeSelectOption}
                disabled={isWatchOnly || !hasUnsignedAct || !passportEventsList?.length}
                onChange={handleHasViolationEvent}
              />
            </FieldBlock>
          </Row>
        )}
        {isPaperless && violation?.inEvent?.value && (
          <ErknmActViolationEventItems
            name={fieldName}
            formRef={formRef}
            passportEvents={passportEventsList}
            watchOnly={isWatchOnly}
            hasUnsignedAct={hasUnsignedAct}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
            id={id}
          />
        )}
      </>
    </KnmCollapse>
  );
};
